/**
 * Owns the one question DiscoveryFeed actually cares about: should MAHI show
 * this person around right now?
 *
 * Split out of the feed because the answer depends on three things that arrive
 * at different times — auth resolving, the deck loading, and a server flag —
 * and folding that into the feed's own render would put four more pieces of
 * state next to the swipe logic.
 *
 * The seen-flag is written optimistically and never awaited. If the write
 * fails the user sees the tour once more, which is a far better failure than
 * blocking the screen behind a bookkeeping call.
 */
import { useCallback, useEffect, useState } from 'react';
import mahi from '../../api';
import { useMahiAuth } from '../../auth';

export default function useDiscoverTour({ user, authLoading, ready }) {
  const { refresh } = useMahiAuth();
  const [active, setActive] = useState(false);
  // Latches once so that finishing the tour (which clears `seen` locally) can
  // never re-open it in the same session, and so a `refresh()` landing
  // mid-tour can't restart it.
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (settled || authLoading || !ready || !user) return;
    if (user.discover_tour_seen_at) { setSettled(true); return; }
    // One frame's grace so the card stage has laid out and the tour can
    // measure real targets rather than dropping steps that exist but haven't
    // been painted yet.
    const t = setTimeout(() => { setActive(true); setSettled(true); }, 350);
    return () => clearTimeout(t);
  }, [settled, authLoading, ready, user]);

  const finish = useCallback(() => {
    setActive(false);
    // Refresh auth after the write so the context carries the flag. Without
    // this, `settled` alone guards the current mount — navigate to the wardrobe
    // and back and the feed remounts against a still-stale `user`, and she
    // starts the tour over on the same session.
    mahi.post('/profile/tour-seen', { seen: true }).then(() => refresh()).catch(() => {});
  }, [refresh]);

  /** Profile panel's "Show me around again". */
  const replay = useCallback(() => {
    setSettled(true);
    setActive(true);
  }, []);

  return { tourActive: active, finishTour: finish, replayTour: replay };
}
